import { TripAnnotation } from "@fairfleet/geotab";
import { endOfDay, startOfDay } from "date-fns";
import { uniq } from "lodash";
import { useMemo } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_TRIP_ANNOTATION } from "@/constants/resultsLimits";
import { usePerDeviceQueries } from "@/hooks/usePerDeviceQueries";
import { ResultsCapError } from "@/utilities/error/ResultsCapError";

/** First query key segment; `useSaveChanges` invalidates it after an edit. */
export const TRIP_ANNOTATIONS_QUERY_KEY = "annotations";

interface Args {
  toDate: Date;
  fromDate: Date;
  deviceIds?: string[];
  /** Set to false to skip the query entirely (callers that never show annotations). */
  enabled?: boolean;
}

/**
 * Annotations (trip reasons) of the given devices within the given days, one query per device so
 * the caller can show how many vehicles are read. A device whose page comes back full fails with
 * {@link ResultsCapError}: a missing reason must not look like a trip without one.
 * Once every vehicle has settled, `data` holds the annotations of the vehicles that were read and
 * `unreadDeviceIds` names the ones that failed, so one failed vehicle does not hide the others.
 */
export function useTripAnnotationForDevices({ toDate, fromDate, deviceIds = [], enabled = true }: Args) {
  const { geotabClient } = useGeotabClient();
  const from = startOfDay(fromDate);
  const to = endOfDay(toDate);

  const perDevice = usePerDeviceQueries<TripAnnotation>({
    queryKey: TRIP_ANNOTATIONS_QUERY_KEY,
    deviceIds: uniq(deviceIds),
    fromDate: from,
    toDate: to,
    enabled,
    fetchRecords: async (deviceId, signal) => {
      const annotations = await geotabClient.get(
        "TripAnnotation",
        { fromDate: from, toDate: to, deviceSearch: { id: deviceId } },
        RESULTS_LIMIT_TRIP_ANNOTATION,
        signal,
      );
      if (annotations.length >= RESULTS_LIMIT_TRIP_ANNOTATION) {
        throw new ResultsCapError("TripAnnotation", RESULTS_LIMIT_TRIP_ANNOTATION);
      }
      return annotations;
    },
  });

  const { byDevice, isPending, error, read, totalCount, failedDeviceIds } = perDevice;
  // Joined to a string so the set keeps its identity while the failures stay the same.
  const failedKey = failedDeviceIds.join("\u0000");
  const unreadDeviceIds = useMemo(() => new Set(failedKey ? failedKey.split("\u0000") : []), [failedKey]);
  const data = useMemo(
    () =>
      isPending
        ? undefined
        : Object.entries(byDevice)
            .filter(([deviceId]) => !unreadDeviceIds.has(deviceId))
            .flatMap(([, annotations]) => annotations),
    [isPending, byDevice, unreadDeviceIds],
  );

  return { data, isPending, error, unreadDeviceIds, progress: { read, totalCount } };
}
